import {
    notification,
    PageHeader,
    Button,
    Form,
    Alert,
    Row,
    Col,
    Input,
    Select,
    Card,
    Spin,
} from 'antd';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdArrowBack } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';
import { getDriverById, oldUpdateDriver } from '../../api/drivers.api';

import { useWorkspaceStore } from '../../store/workspace.store';
import { IDriver } from '../../interfaces/driver.interface';
import { PhoneNumber } from '../../widgets/PhoneNumber';
import { ECountryCodes } from '../../enums/country-code.enum';
import { useAuthStore } from '../../store/auth.store';

const { Option } = Select;

const DriverUpdate: FC = () => {
    const { t } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });
    const { t: tr } = useTranslation('shared', { keyPrefix: 'RESPONSE_ACTIONS' });
    const { t: ca } = useTranslation('onboarding', { keyPrefix: 'CREATE_ACCOUNT' });

    const params = useParams();
    const workspace = useWorkspaceStore((state) => state.data);
    const user = useAuthStore((state) => state.data);

    const [form] = Form.useForm();
    const [error] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [driver, setDriver] = useState<IDriver>();
    const [defaultCountryCode, setDefaultCountryCode] = useState<string>(
        /* @ts-ignore */
        `+${ECountryCodes[user.countryCode] || '+593'}`,
    );

    const navigate = useNavigate();

    const fieldProps = {
        label: ca('PHONE'),
        keyCountryCode: 'countryCode',
        keyMobile: 'mobile',
    };

    const getDriver = () => {
        setLoading(true);
        getDriverById(params?.id as string)
            .then((response) => {
                setDriver(response.data);
            })
            .catch((error: any) => {
                const description = error.response.data.message;
                notification.error({
                    message: t('DRIVER_NOT_OBTAINED'),
                    description,
                });
            })
            .finally(() => setLoading(false));
    };

    const onSubmit = async (values: any) => {
        const payload = {
            ...values,
            userID: params.id,
            smallFleetID: workspace?._id,
        };
        oldUpdateDriver(payload)
            .then(() => {
                notification.success({
                    message: t('DRIVER_UPDATED'),
                    description: '',
                });
            })
            .catch((error: any) => {
                const description = error.response.data.message;
                notification.error({
                    message: t('DRIVER_NO_UPDATED'),
                    description,
                });
            })
            .finally(() => setLoading(false));
    };

    const handleSelect = (value: string) => {
        form.setFieldValue('countryCode', value);
        setDefaultCountryCode(value);
    };

    useEffect(() => {
        const countryCode = user?.countryCode || '+593';
        handleSelect(countryCode);
    }, [user]);

    useEffect(() => {
        if (!params.id) return;
        getDriver();
    }, [workspace?._id, params.id]);

    useEffect(() => {
        if (!driver) return;
        form.setFieldsValue({
            ...driver,
        });
        setDefaultCountryCode(driver?.countryCode || '+593');
    }, [driver]);

    return (
        <div className="screen">
            <PageHeader
                title={`${t('DRIVER')} | ${driver?.name || ''} ${driver?.lastName || ''}`}
                backIcon={(
                    <IoMdArrowBack
                        size={20}
                        color="#6C8EEF"
                        style={{ verticalAlign: 'middle' }}
                    />
                )}
                style={{ padding: 0, width: '100%' }}
                onBack={() => navigate('/drivers')}
                extra={(
                    <Button
                        type="primary"
                        size="small"
                        loading={loading}
                        onClick={() => form.submit()}
                    >
                        {t('UPDATE')}
                    </Button>
                )}
                footer={(
                    <div className="section">
                        {
                            loading ? (
                                <Spin />
                            ) : (
                                <Form
                                    form={form}
                                    onFinish={onSubmit}
                                    layout="vertical"
                                >
                                    {
                                        error && (
                                            <Alert
                                                message={error}
                                                type="error"
                                                style={{ marginBottom: 16 }}
                                            />
                                        )
                                    }

                                    <Row align="top" justify="space-between" gutter={[24, 20]}>
                                        <Col xs={24} xxl={12}>
                                            <Card>
                                                <Row align="top" justify="space-between" gutter={[24, 20]}>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            name="name"
                                                            label={ca('FIRST_NAME')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Input
                                                                placeholder={ca('FIRST_NAME')}
                                                            />
                                                        </Form.Item>
                                                        <Form.Item
                                                            name="email"
                                                            label={ca('EMAIL')}
                                                            rules={[{ type: 'email', message: tr('REQUIRED'), required: true }]}
                                                        >
                                                            <Input
                                                                placeholder={ca('EMAIL')}
                                                            />
                                                        </Form.Item>

                                                        {/* <Form.Item
                                                            name="language"
                                                            label={t('LANGUAGE')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Select className="signup-select">
                                                                <Option value="ES">
                                                                    {t('SPANISH')}
                                                                </Option>
                                                                <Option value="EN">
                                                                    {t('ENGLISH')}
                                                                </Option>
                                                            </Select>
                                                        </Form.Item> */}
                                                    </Col>

                                                    <Col span={12}>
                                                        <Form.Item
                                                            name="lastName"
                                                            label={ca('LAST_NAME')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Input
                                                                placeholder={ca('LAST_NAME')}
                                                            />
                                                        </Form.Item>

                                                        <PhoneNumber
                                                            form={form}
                                                            fieldProps={fieldProps}
                                                            handleSelect={handleSelect}
                                                            defaultCountryCode={defaultCountryCode}
                                                        />

                                                        {/* <Form.Item
                                                            name="currency"
                                                            label={t('CURRENCY')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Select className="signup-select" options={CURRENCIES} />
                                                        </Form.Item> */}
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                        <Col xs={24} xxl={12}>
                                            <Card>
                                                <Row align="top" justify="space-between" gutter={[24, 20]}>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            name="vehicleModelName"
                                                            label={t('VEHICLE_MODEL_NAME')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Input
                                                                placeholder={t('VEHICLE_MODEL_NAME')}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            name="MCNumber"
                                                            label={t('MC_NUMBER')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Input
                                                                placeholder={t('MC_NUMBER')}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Row align="top" justify="space-between" gutter={[24, 20]}>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            name="vehicleColor"
                                                            label={t('VEHICLE_COLOR')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Input
                                                                placeholder={t('VEHICLE_COLOR')}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            name="vehicleYear"
                                                            label={t('VEHICLE_YEAR')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Input
                                                                placeholder={t('VEHICLE_YEAR')}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Row align="top" justify="space-between" gutter={[24, 20]}>
                                                    <Col span={24}>
                                                        <Form.Item
                                                            name="vehicleType"
                                                            label={t('VEHICLE_TYPE')}
                                                            rules={[{ required: true, message: tr('REQUIRED') }]}
                                                        >
                                                            <Select className="signup-select">
                                                                <Option value="LITE">{t('CAR')}</Option>
                                                                <Option value="BIKE">{t('BIKE')}</Option>
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Form>
                            )
                        }
                    </div>
                )}
            />
        </div>
    );
};

export { DriverUpdate };
